import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSession } from "@/lib/session";
import { LOCATIONS, LOCATIONS_GR } from "@/lib/suppliers";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock, Activity, User2, Clock, Store } from "lucide-react";

function fmtTime(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
}

export default function LiveView() {
  const { liveViewUnlocked, unlockLiveView } = useSession();
  const [auth, setAuth] = useState({ username: "", password: "" });
  const [activeShifts, setActiveShifts] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shifts/active");
      setActiveShifts(data || []);
    } catch {
      /* swallow — banner stays as-is */
    } finally {
      setLoading(false);
    }
  };

  // Strict 6-second polling loop (6000ms)
  useEffect(() => {
    if (!liveViewUnlocked) return;
    load();
    const intervalId = setInterval(load, 6000);
    return () => clearInterval(intervalId);
  }, [liveViewUnlocked]);

  if (!liveViewUnlocked) {
    const submit = async () => {
      try {
        await api.post("/liveview/auth", {
          username: (auth.username || "").trim().toLowerCase(),
          password: (auth.password || "").trim().toLowerCase(),
        });
        unlockLiveView();
        toast.success("Dashboard unlocked");
      } catch {
        toast.error("Invalid credentials");
      }
    };
    return (
      <Card className="max-w-md mx-auto mt-12 p-8" data-testid="live-view-gate">
        <div className="flex items-center gap-3 mb-4">
          <Lock className="h-6 w-6 text-slate-900" />
          <h2 className="text-xl font-bold text-slate-900">Owner Access Required</h2>
        </div>
        <p className="text-sm text-slate-500 mb-5">
          Enter the master admin credentials to view the active shift roster.
        </p>
        <Input
          placeholder="Username"
          value={auth.username}
          onChange={(e) => setAuth({ ...auth, username: e.target.value })}
          className="mb-3 min-h-[52px]"
          data-testid="live-view-username"
          autoFocus
        />
        <Input
          type="password"
          placeholder="Password"
          value={auth.password}
          onChange={(e) => setAuth({ ...auth, password: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          className="mb-4 min-h-[52px]"
          data-testid="live-view-password"
        />
        <Button
          onClick={submit}
          className="w-full min-h-[56px] bg-slate-900 hover:bg-slate-800 font-bold"
          data-testid="live-view-submit"
        >
          Unlock Dashboard
        </Button>
      </Card>
    );
  }

  const onlineCount = activeShifts.length;

  return (
    <div data-testid="live-view-roster">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
            Active Shift Roster
          </h1>
          <p className="text-slate-500 mt-1 tracking-wide">
            Ζωντανή Παρακολούθηση Βαρδιών · {onlineCount} register(s) online · auto-refresh 6s {loading && "· syncing…"}
          </p>
        </div>
        <div className="flex items-center gap-2 text-emerald-700 font-mono text-sm bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-md" data-testid="live-pulse">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-600"></span>
          </span>
          LIVE · 6s
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="live-grid">
        {LOCATIONS.map((loc) => (
          <KioskCard
            key={loc}
            location={loc}
            shifts={activeShifts.filter((s) => s.location === loc)}
          />
        ))}
      </div>
    </div>
  );
}

function KioskCard({ location, shifts }) {
  const isOnline = shifts.length > 0;
  return (
    <Card
      data-testid={`kiosk-card-${location.toLowerCase()}`}
      className={`p-6 transition-colors ${
        isOnline
          ? "border-emerald-200 bg-gradient-to-br from-emerald-50 to-white"
          : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${
            isOnline ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-500"
          }`}>
            <Store className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 leading-tight">{location}</h2>
            <p className="text-xs text-slate-400 leading-tight">{LOCATIONS_GR[location]}</p>
          </div>
        </div>
        {isOnline ? (
          <span
            data-testid={`kiosk-status-${location.toLowerCase()}-online`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest bg-emerald-100 text-emerald-700 border border-emerald-200"
          >
            🟢 ONLINE · {shifts.length}
          </span>
        ) : (
          <span
            data-testid={`kiosk-status-${location.toLowerCase()}-offline`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest bg-slate-100 text-slate-500 border border-slate-200"
          >
            ⚪ OFFLINE
          </span>
        )}
      </div>

      <div className="space-y-2">
        {[1, 2].map((r) => {
          const s = shifts.find((x) => x.register === r);
          return (
            <div
              key={r}
              data-testid={`kiosk-${location.toLowerCase()}-r${r}`}
              className={`flex items-center gap-3 p-3 rounded-xl border ${
                s ? "bg-white border-emerald-200" : "bg-slate-50 border-slate-200"
              }`}
            >
              <div
                className={`h-11 w-11 rounded-lg flex items-center justify-center shrink-0 ${
                  s ? "bg-emerald-600 text-white" : "bg-slate-300 text-slate-600"
                }`}
              >
                <User2 className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-slate-900">Register {r}</span>
                  {s && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                      <Activity className="h-3 w-3" />
                      ACTIVE
                    </span>
                  )}
                </div>
                {s ? (
                  <div className="text-xs font-mono text-slate-600 truncate" data-testid={`kiosk-${location.toLowerCase()}-r${r}-details`}>
                    {s.employee_name} · €{Number(s.starting_float || 0).toFixed(2)} float ·{" "}
                    <Clock className="inline h-2.5 w-2.5 mb-0.5" /> {fmtTime(s.started_at)}
                  </div>
                ) : (
                  <div className="text-xs font-mono text-slate-400">⚪ OFFLINE · No active shift</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
